import { useEffect, useRef, useState } from 'react';
import { findPageByLegacyFile, pages, resolveLegacyHref } from '../content/pages';
import { getPageMeta, getBlogPosts } from '../content/meta';
import { getJsonLdGraph } from '../content/structuredData';
import { SITE_NAME, SITE_LOCALE, DEFAULT_OG_IMAGE, TWITTER_SITE_HANDLE, absoluteUrl } from '../content/site';

const LEGACY_ROOT = '/legacy-pages/';
const MANAGED_ATTRIBUTE = 'data-tohu-bohu-legacy';
const JSONLD_ATTRIBUTE = 'data-tohu-bohu-jsonld';

function absoluteAssetUrl(value) {
  return new URL(value, `${window.location.origin}/`).href;
}

function upsertMeta(selector, attributes) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([name, value]) => element.setAttribute(name, value));
}

function upsertJsonLd(graph) {
  document.querySelectorAll(`script[${JSONLD_ATTRIBUTE}]`).forEach((node) => node.remove());
  if (!graph) return;
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute(JSONLD_ATTRIBUTE, '');
  script.textContent = JSON.stringify(graph);
  document.head.appendChild(script);
}

function applyMetadata(parsedDocument, page) {
  // src/content/meta.js est la source de vérité (title/description uniques et
  // optimisés pour les 20 pages). On ne retombe sur le <head> de la maquette
  // legacy que si une page n'a pas (encore) d'entrée dans le registre.
  const registryMeta = getPageMeta(page.path);
  const parsedTitle = parsedDocument.querySelector('title')?.textContent?.trim();
  const parsedDescription = parsedDocument.querySelector('meta[name="description"]')?.getAttribute('content');

  const title = registryMeta?.title || parsedTitle;
  const description = registryMeta?.description || parsedDescription;
  const ogType = registryMeta?.ogType || (page.path.startsWith('/blog/') ? 'article' : 'website');
  const url = absoluteUrl(page.path);

  if (title) document.title = title;

  let descriptionElement = document.querySelector('meta[name="description"]');
  if (!descriptionElement) {
    descriptionElement = document.createElement('meta');
    descriptionElement.name = 'description';
    document.head.appendChild(descriptionElement);
  }
  if (description) descriptionElement.content = description;

  if (title) upsertMeta('meta[property="og:title"]', { property: 'og:title', content: title });
  if (description) upsertMeta('meta[property="og:description"]', { property: 'og:description', content: description });
  upsertMeta('meta[property="og:type"]', { property: 'og:type', content: ogType });
  upsertMeta('meta[property="og:url"]', { property: 'og:url', content: url });
  upsertMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: SITE_NAME });
  upsertMeta('meta[property="og:locale"]', { property: 'og:locale', content: SITE_LOCALE });
  if (DEFAULT_OG_IMAGE) {
    upsertMeta('meta[property="og:image"]', { property: 'og:image', content: absoluteUrl(DEFAULT_OG_IMAGE) });
  }

  upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: DEFAULT_OG_IMAGE ? 'summary_large_image' : 'summary' });
  if (title) upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: title });
  if (description) upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: description });
  if (DEFAULT_OG_IMAGE) {
    upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: absoluteUrl(DEFAULT_OG_IMAGE) });
  }
  if (TWITTER_SITE_HANDLE) upsertMeta('meta[name="twitter:site"]', { name: 'twitter:site', content: TWITTER_SITE_HANDLE });

  let canonical = document.head.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.rel = 'canonical';
    document.head.appendChild(canonical);
  }
  canonical.href = url;

  if (registryMeta) {
    const blogPosts = registryMeta.schemas?.includes('blogIndex') ? getBlogPosts(pages) : undefined;
    upsertJsonLd(getJsonLdGraph(page, registryMeta, { blogPosts }));
  } else {
    upsertJsonLd(null);
  }
}

function installStyles(parsedDocument) {
  const installed = [];
  const pending = [];
  parsedDocument.querySelectorAll('style, link[rel="stylesheet"]').forEach((source) => {
    const element = document.createElement(source.tagName.toLowerCase());
    element.setAttribute(MANAGED_ATTRIBUTE, '');
    if (source.tagName === 'STYLE') {
      element.textContent = source.textContent;
    } else {
      [...source.attributes].forEach(({ name, value }) => {
        element.setAttribute(name, name === 'href' ? absoluteAssetUrl(value) : value);
      });
      pending.push(new Promise((resolve) => {
        element.addEventListener('load', resolve, { once: true });
        element.addEventListener('error', resolve, { once: true });
        window.setTimeout(resolve, 3000);
      }));
    }
    document.head.appendChild(element);
    installed.push(element);
    source.remove();
  });
  return { installed, ready: Promise.all(pending) };
}

async function executeScripts(scripts) {
  const installed = [];
  for (const source of scripts) {
    const script = document.createElement('script');
    script.setAttribute(MANAGED_ATTRIBUTE, '');
    [...source.attributes].forEach(({ name, value }) => {
      if (name === 'src') script.src = absoluteAssetUrl(value);
      else if (name !== 'defer') script.setAttribute(name, value);
    });

    const completion = source.src
      ? new Promise((resolve) => {
          script.addEventListener('load', resolve, { once: true });
          script.addEventListener('error', resolve, { once: true });
        })
      : Promise.resolve();

    if (!source.src) script.textContent = source.textContent;
    document.body.appendChild(script);
    installed.push(script);
    await completion;
  }
  return installed;
}

function resolveInternalDestination(rawHref) {
  if (!rawHref || rawHref.startsWith('#')) return null;
  const url = new URL(rawHref, window.location.href);
  if (url.origin !== window.location.origin) return null;
  const fileName = url.pathname.split('/').pop();
  const page = findPageByLegacyFile(fileName);
  return page ? `${page.path}${url.hash}` : null;
}

export default function LegacyPage({ page }) {
  const contentRef = useRef(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    let cancelled = false;
    let installedStyles = [];
    let installedScripts = [];
    let removeNavigationHandler = () => {};
    const previousBodyClass = document.body.className;
    const previousBodyStyle = document.body.getAttribute('style');

    async function loadPage() {
      setStatus('loading');
      try {
        const response = await fetch(`${LEGACY_ROOT}${page.file}`);
        if (!response.ok) throw new Error(`Impossible de charger ${page.file} (${response.status})`);
        const source = await response.text();
        if (cancelled) return;

        const parsed = new DOMParser().parseFromString(source, 'text/html');

        // Maillage interne : les maquettes legacy utilisent des hrefs relatifs vers
        // d'anciens fichiers plats (ex: "produit.html"). On les réécrit vers
        // les routes canoniques directement dans le DOM injecté,
        // pour qu'un crawler qui lit le HTML rendu (sans simuler de clics, ex:
        // Googlebot en extraction de liens) suive un maillage correct — pas
        // seulement le HTML statique prérendu (scripts/prerender.mjs applique la
        // même réécriture, via la même fonction pure resolveLegacyHref).
        parsed.querySelectorAll('a[href]').forEach((anchor) => {
          const href = anchor.getAttribute('href');
          const resolved = resolveLegacyHref(href, anchor.textContent);
          if (resolved !== href) anchor.setAttribute('href', resolved);
        });

        const scripts = [...parsed.querySelectorAll('script')];
        scripts.forEach((script) => script.remove());
        const styles = installStyles(parsed);
        installedStyles = styles.installed;
        applyMetadata(parsed, page);

        document.documentElement.lang = parsed.documentElement.lang || 'fr';
        document.body.className = parsed.body.className;
        const bodyStyle = parsed.body.getAttribute('style');
        if (bodyStyle) document.body.setAttribute('style', bodyStyle);
        else document.body.removeAttribute('style');

        contentRef.current.innerHTML = parsed.body.innerHTML;
        await styles.ready;
        installedScripts = await executeScripts(scripts);
        if (cancelled) return;

        const navigationHandler = (event) => {
          if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          const anchor = event.target.closest?.('a[href]');
          if (!anchor || anchor.hasAttribute('download') || anchor.target === '_blank') return;
          const destination = resolveInternalDestination(anchor.getAttribute('href'));
          if (!destination) return;
          event.preventDefault();
          window.location.assign(destination);
        };
        document.addEventListener('click', navigationHandler);
        removeNavigationHandler = () => document.removeEventListener('click', navigationHandler);

        setStatus('ready');
        requestAnimationFrame(() => {
          if (window.location.hash) {
            const targetId = decodeURIComponent(window.location.hash.slice(1));
            document.getElementById(targetId)?.scrollIntoView();
          }
          else window.scrollTo(0, 0);
        });
      } catch (error) {
        console.error(error);
        if (!cancelled) setStatus('error');
      }
    }

    loadPage();

    return () => {
      cancelled = true;
      removeNavigationHandler();
      installedStyles.forEach((element) => element.remove());
      installedScripts.forEach((element) => element.remove());
      document.querySelectorAll(`script[${JSONLD_ATTRIBUTE}]`).forEach((element) => element.remove());
      if (contentRef.current) contentRef.current.innerHTML = '';
      document.body.className = previousBodyClass;
      if (previousBodyStyle === null) document.body.removeAttribute('style');
      else document.body.setAttribute('style', previousBodyStyle);
    };
  }, [page.file]);

  return (
    <>
      {status === 'loading' && <div className="route-status">Chargement de Tohu Bohu…</div>}
      {status === 'error' && (
        <main className="route-error">
          <span>Tohu Bohu</span>
          <h1>Cette page n’a pas pu être chargée.</h1>
          <p>La maquette source est momentanément indisponible.</p>
          <a href="/">Retour à l’accueil →</a>
        </main>
      )}
      <div ref={contentRef} data-page={page.path} hidden={status === 'error'} />
    </>
  );
}
